import { FC } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { Badge, types } from "@vilocnv/allsetra-core";
import { round } from "lodash";
import moment from "moment";
import {
  OBJECT_RIDE_MODES,
  OBJECT_RIDE_MODES_COLOR
} from "app/data/helpers/objectRideHelpers";
import { MileageIcon } from "assets/icons";
import { getAddressInfo } from "components/sections/objects/ObjectRidesSection/addressHelpers";

interface LastTripsPanelItemProps {
  trip: types.IObjectRide;
  selected: boolean;
  onSelect: (trip: types.IObjectRide) => void;
  translator: (key: string) => string;
  currentLocale: string;
}

const resolveTripAddressLabel = ({
  address,
  latitude,
  longitude,
  rideMode,
  translator
}: {
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  rideMode: number;
  translator: (key: string) => string;
}) => {
  const addressInfo = getAddressInfo(latitude, longitude, rideMode);

  if (addressInfo.isHidden) {
    return translator("typography.addressIsHidden");
  }

  if (addressInfo.isUnknown) {
    return translator("typography.addressUnknown");
  }

  return address || translator("typography.na");
};

const LastTripsPanelItem: FC<LastTripsPanelItemProps> = ({
  trip,
  selected,
  onSelect,
  translator,
  currentLocale
}) => {
  const startLabel = moment(trip.startDate)
    .locale(currentLocale)
    .format("MMM Do, YYYY, HH:mm");
  const endTimeLabel = trip.endDate
    ? moment(trip.endDate).locale(currentLocale).format("HH:mm")
    : null;

  const startAddress = resolveTripAddressLabel({
    address: trip.correctedStartAddress || trip.startAddress,
    latitude: trip.correctedStartLatitude ?? trip.startLatitude,
    longitude: trip.correctedStartLongitude ?? trip.startLongitude,
    rideMode: trip.rideMode,
    translator
  });
  const endAddress = resolveTripAddressLabel({
    address: trip.correctedEndAddress || trip.endAddress,
    latitude: trip.correctedEndLatitude ?? trip.endLatitude,
    longitude: trip.correctedEndLongitude ?? trip.endLongitude,
    rideMode: trip.rideMode,
    translator
  });

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onSelect(trip)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(trip);
        }
      }}
      sx={{
        p: 2,
        borderBottom: "1px solid #E8ECF2",
        cursor: "pointer",
        backgroundColor: selected ? "#EFF4FF" : "#FFFFFF",
        borderLeft: selected ? "3px solid #498FEB" : "3px solid transparent",
        "&:hover": {
          backgroundColor: selected ? "#EFF4FF" : "#F9FBFF"
        }
      }}
    >
      <Typography variant="body2" fontWeight={600} color="#1D1F2B">
        {endTimeLabel ? `${startLabel} - ${endTimeLabel}` : startLabel}
      </Typography>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
        <Badge colorScheme={OBJECT_RIDE_MODES_COLOR[trip.rideMode] ?? "error"}>
          {OBJECT_RIDE_MODES(translator)[trip.rideMode] ?? "-"}
        </Badge>
        <Badge colorScheme="blank" icon={<MileageIcon />} textVariant="small">
          {`${round(trip.mileage || 0, 1)} km`}
        </Badge>
      </Stack>

      <Typography
        variant="caption"
        color="#646D7B"
        sx={{ display: "block", mt: 1.5, lineHeight: 1.4 }}
      >
        {startAddress}
      </Typography>
      <Typography variant="caption" color="#646D7B" sx={{ display: "block" }}>
        → {endAddress}
      </Typography>
    </Box>
  );
};

export default LastTripsPanelItem;
